/**
 * The sign-in screen. Handover rev7 §11.12.1.
 *
 * **Google sign-in is the route**, on every deployed project including the
 * test one. The button starts the Supabase Auth OAuth flow against the
 * project, and the session that comes back is what every later call carries on
 * `Authorization: Bearer`.
 *
 * **The seeded session is the local stack's, and nowhere else's.** It was
 * offered wherever the bundle's `APP_ENV` read `local` or `test`, and a
 * deployed test bundle carries `test` — so on 6 October the deployed page
 * offered one-click admin sign-in to anyone on the internet. The test is now
 * the project the bundle talks to: a loopback host, which no deployed project
 * can be. The refusal that matters is still the server's and is unreachable
 * from here — `public.staff_sign_in_seeded` refuses anywhere
 * `public.is_local_stack()` is not true (20261006160000), whatever any label
 * says and whatever `public.staff` holds.
 */

import { useState } from 'react'

import { consoleCall, ConsoleError } from './api.ts'
import { useConsole } from './context.ts'
import { rememberReturnPath, SEEDED_STAFF, seededLoginOffered, type ConsoleStaff, type ConsoleView } from './session.ts'

interface SignInAnswer {
  ok: true
  provider: 'google' | 'seeded'
  staff: ConsoleStaff
  views: ConsoleView[]
  app_env?: string
  session?: { token: string; expires_at: string }
}

export function SignIn({ appEnv }: { appEnv: string }): JSX.Element {
  const { config, setSession } = useConsole()
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const seeded = seededLoginOffered(config.supabaseUrl)

  async function signInSeeded(email: string): Promise<void> {
    setBusy(email)
    setError(null)
    try {
      const answer = await consoleCall<SignInAnswer>(
        'sign_in_seeded',
        { email },
        { config, session: null },
      )
      setSession({
        provider: 'seeded',
        staff: answer.staff,
        views: answer.views,
        appEnv: answer.app_env ?? appEnv,
        token: answer.session?.token,
        expiresAt: answer.session?.expires_at,
      })
    } catch (cause) {
      setError(cause instanceof ConsoleError ? cause.message : 'A bejelentkezés nem sikerült.')
    } finally {
      setBusy(null)
    }
  }

  /**
   * Google sign-in, through Supabase Auth's own authorize endpoint. No
   * `@supabase/supabase-js` is pulled in for it: the flow is one redirect out
   * and one session back, and a client library would arrive with a storage
   * layer, a realtime socket and an opinion about where the key goes — and
   * this project is careful about exactly that.
   */
  function signInGoogle(): void {
    // Run 12 item G: a deep link from a staff email lands on its page after
    // the round trip, not on the console home.
    const here = globalThis.location
    if (here) rememberReturnPath(`${here.pathname}${here.search}${here.hash}`)
    const back = `${globalThis.location?.origin ?? ''}/staff`
    const url = new URL('/auth/v1/authorize', config.supabaseUrl)
    url.searchParams.set('provider', 'google')
    url.searchParams.set('redirect_to', back)
    globalThis.location?.assign(url.toString())
  }

  return (
    <div className="plt-console" data-testid="console-signin">
      <div className="signin">
        <h2>Planteen · munkatársi konzol</h2>
        <p className="lead">
          Belépés Google-fiókkal. A hozzáférést a hozzáférési táblázat szabályozza, és a
          jogosultságokat minden lépésnél a kiszolgáló ellenőrzi.
        </p>

        {error !== null && <p className="note error" data-testid="signin-error">{error}</p>}

        <button type="button" className="primary" onClick={signInGoogle} data-testid="signin-google">
          Belépés Google-fiókkal
        </button>

        {seeded && (
          <>
            <p className="note gap" data-testid="seeded-notice">
              Ez a helyi fejlesztői környezet ({appEnv}), ezért a három tesztfelhasználó
              közvetlenül is beléphet. Telepített környezetben — a tesztkörnyezetet is beleértve —
              ez a belépés nem létezik, ott a Google-bejelentkezés az egyetlen út.
            </p>
            <ul>
              {SEEDED_STAFF.map((person) => (
                <li key={person.email}>
                  <button
                    type="button"
                    disabled={busy !== null}
                    onClick={() => void signInSeeded(person.email)}
                    data-testid={`signin-${person.email}`}
                  >
                    {person.email}
                    <small>{person.hint}</small>
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  )
}
